import type { BusinessFlowLane, BusinessFlowLaneStep } from '@/types/knowledge_graph'
import { cn } from '@/lib/utils'
import { FlowStepBubble } from '../components/FlowStepBubble'

interface Props {
  lanes: BusinessFlowLane[]
}

function stepBubbleClass (step: BusinessFlowLaneStep): string {
  if (step.step_type === 'exception') return 'warn'
  if (step.step_type === 'loop' || step.step_type === 'parallel') return 'ok'
  return 'ok'
}

export function BusinessFlowLanesView ({ lanes }: Props): React.ReactElement {
  if (lanes.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--bp-ink3)', fontSize: 14 }}>
        業務フローがありません
      </div>
    )
  }

  return (
    <>
      {lanes.map((lane) => (
        <div key={lane.id} style={{ marginBottom: 24 }}>
          <div className='section-head'>
            <h3>{lane.name}</h3>
            <span className='count'>{lane.actors.length} アクター / {lane.steps.length} ステップ</span>
          </div>

          {lane.steps.length > 0 && (
            <div className='flow-lane-wrap'>
              <div className='flow-lane'>
                <div style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.06em',
                  textTransform: 'uppercase',
                  color: 'var(--bp-ok)',
                  marginBottom: 10,
                  paddingLeft: 124
                }}
                >
                  ▸ 正常系
                </div>

                {lane.actors.map((actor, actorIdx) => {
                  const actorSteps = lane.steps.filter((s) => s.actor === actor)
                  if (actorSteps.length === 0 && actorIdx !== 0) return null
                  return (
                    <div key={actor}>
                      <div className='flow-actor-row'>
                        <div className='flow-actor-label'>{actor}</div>
                        <div className='flow-events'>
                          {lane.steps.map((step, si) => {
                            const isActor = step.actor === actor
                            return (
                              <div key={si} style={{ display: 'flex', alignItems: 'center' }}>
                                {si > 0 && isActor && <div className='flow-arrow'>→</div>}
                                {isActor
                                  ? (
                                    <div className='flow-event-node'>
                                      <FlowStepBubble step={step} className={cn('flow-event-bubble', stepBubbleClass(step))} />
                                    </div>
                                    )
                                  : (
                                    <div style={{ width: si === 0 ? 110 : 80 }} />
                                    )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                      {actorIdx < lane.actors.length - 1 && (
                        <div className='flow-section-divider' />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {lane.exception_steps.length > 0 && (
            <>
              <div style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--bp-warning)',
                margin: '20px 0 10px',
                paddingLeft: 124
              }}
              >
                ▸ 例外経路
              </div>

              <div className='flow-lane-wrap'>
                <div className='flow-lane'>
                  {(() => {
                    const exActors = [...new Set(lane.exception_steps.map((s) => s.actor))]
                    return exActors.map((actor, actorIdx) => (
                      <div key={actor}>
                        <div className='flow-actor-row'>
                          <div className='flow-actor-label'>{actor}</div>
                          <div className='flow-events'>
                            {lane.exception_steps.filter((s) => s.actor === actor).map((step, si) => (
                              <div key={si} style={{ display: 'flex', alignItems: 'center' }}>
                                {si > 0 && <div className='flow-arrow'>→</div>}
                                <div className='flow-event-node'>
                                  <FlowStepBubble step={step} className='flow-event-bubble warn' />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {actorIdx < exActors.length - 1 && (
                          <div className='flow-section-divider' />
                        )}
                      </div>
                    ))
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      ))}
    </>
  )
}
